/**
 * @fileoverview Modal component for managing Trivia Sets in Adivina Adivinador (Riddle).
 * Allows operator to create, edit, list, and delete trivia packages, manage individual questions,
 * and set a package as the active trivia.
 * 
 * @module components/Admin/Games/Riddle/RiddleTriviaModal
 */

import React, { useState, useEffect } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage';
import { Confirm } from './../../../Commons/Modals/Confirm';
import { RiddleQuestionFormModal } from './RiddleQuestionFormModal';
import {
    getTriviaSets,
    getTriviaSetById,
    saveTriviaSet,
    deleteTriviaSet,
    deleteQuestion,
    updateSettings,
    getSettings
} from './../../../../utilities/BL/Admin/Games/Riddle';

/**
 * Resolves a media path into an absolute preview URL.
 */
const resolveMediaUrl = (mediaPath) => {
    if (!mediaPath) return '';
    if (mediaPath.startsWith('http://') || mediaPath.startsWith('https://') || mediaPath.startsWith('blob:')) {
        return mediaPath;
    }
    const apiBaseUrl = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
    return `${apiBaseUrl}${mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath}`;
};

/**
 * Riddle Trivia Management Modal Component.
 * 
 * @function RiddleTriviaModal
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is visible.
 * @param {Function} props.onClose - Close handler.
 * @param {string|null} props.currentActiveTriviaId - ID of currently active trivia in settings.
 * @param {Function} props.onActiveTriviaChanged - Callback when active trivia changes.
 * @returns {React.ReactElement|null}
 */
export const RiddleTriviaModal = ({
    isOpen,
    onClose,
    currentActiveTriviaId = null,
    onActiveTriviaChanged = () => {}
}) => {
    const { showMessage } = useFloatMessage();

    // Data state
    const [triviaSets, setTriviaSets] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedTriviaDetail, setSelectedTriviaDetail] = useState(null);
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);

    // Form creation/editing state for Trivia Set
    const [isEditingTriviaSet, setIsEditingTriviaSet] = useState(false);
    const [triviaSetFormData, setTriviaSetFormData] = useState({
        id: undefined,
        title: '',
        description: '',
        isActive: true
    });
    const [isSavingTriviaSet, setIsSavingTriviaSet] = useState(false);

    // Question Form Modal state
    const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
    const [questionToEdit, setQuestionToEdit] = useState(null);

    // Confirm Modal state
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({
        message: '',
        actionToYesButton: null,
        textYesButton: 'Sí, eliminar',
        textNoButton: 'Cancelar'
    });

    /**
     * Loads trivia sets from the API.
     */
    const loadTriviaSets = async () => {
        setIsLoading(true);
        try {
            const response = await getTriviaSets();
            if (response.success && Array.isArray(response.data)) {
                setTriviaSets(response.data);
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: response.message || 'Error al obtener los sets de trivia.'
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al cargar trivias.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Loads detailed questions for a specific trivia set.
     */
    const loadTriviaDetail = async (triviaId) => {
        if (!triviaId) return;
        setIsLoadingDetail(true);
        try {
            const response = await getTriviaSetById(triviaId);
            if (response.success && response.data) {
                setSelectedTriviaDetail(response.data);
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: response.message || 'Error al obtener detalle de la trivia.'
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error al cargar preguntas de la trivia.'
            });
        } finally {
            setIsLoadingDetail(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            loadTriviaSets();
            setSelectedTriviaDetail(null);
            setIsEditingTriviaSet(false);
        }
    }, [isOpen]);

    // Handle Escape Key
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen && !isQuestionModalOpen && !showConfirm) {
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, isQuestionModalOpen, showConfirm, onClose]);

    if (!isOpen) return null;

    /**
     * Starts creating a new trivia set.
     */
    const handleStartCreateTriviaSet = () => {
        setTriviaSetFormData({
            id: undefined,
            title: '',
            description: '',
            isActive: true
        });
        setIsEditingTriviaSet(true);
    };

    /**
     * Starts editing an existing trivia set.
     */
    const handleStartEditTriviaSet = (trivia) => {
        setTriviaSetFormData({
            id: trivia.id,
            title: trivia.title || '',
            description: trivia.description || '',
            isActive: trivia.isActive !== false
        });
        setIsEditingTriviaSet(true);
    };

    /**
     * Saves a trivia set (create or update).
     */
    const handleSaveTriviaSet = async (e) => {
        if (e) e.preventDefault();

        if (!triviaSetFormData.title.trim()) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'El título de la trivia es requerido.'
            });
            return;
        }

        setIsSavingTriviaSet(true);
        try {
            const response = await saveTriviaSet(triviaSetFormData);
            if (response.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: triviaSetFormData.id ? 'Trivia actualizada exitosamente.' : 'Trivia creada exitosamente.'
                });
                setIsEditingTriviaSet(false);
                await loadTriviaSets();
                if (selectedTriviaDetail && selectedTriviaDetail.id === triviaSetFormData.id) {
                    await loadTriviaDetail(triviaSetFormData.id);
                }
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: response.message || 'Error al guardar la trivia.'
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error inesperado al guardar la trivia.'
            });
        } finally {
            setIsSavingTriviaSet(false);
        }
    };

    /**
     * Opens confirmation modal to delete a trivia set.
     */
    const handlePromptDeleteTriviaSet = (trivia) => {
        setConfirmConfig({
            message: `¿Estás seguro de que deseas eliminar el paquete de trivias "${trivia.title}"? Esta acción no se puede deshacer.`,
            actionToYesButton: async () => {
                const response = await deleteTriviaSet(trivia.id);
                if (response.success) {
                    showMessage({
                        type: MESSAGE_TYPE.SUCCESS,
                        text: 'Trivia eliminada correctamente.'
                    });
                    if (selectedTriviaDetail?.id === trivia.id) {
                        setSelectedTriviaDetail(null);
                    }
                    await loadTriviaSets();
                    return { success: true };
                } else {
                    return { success: false, message: response.message || 'Error al eliminar la trivia.' };
                }
            },
            textYesButton: 'Sí, eliminar',
            textNoButton: 'Cancelar'
        });
        setShowConfirm(true);
    };

    /**
     * Sets a trivia set as the active one.
     */
    const handleSetActiveTrivia = async (triviaId) => {
        try {
            const currentSettingsResponse = await getSettings();
            const currentSettings = currentSettingsResponse.success ? currentSettingsResponse.data : {};

            const updatePayload = {
                ...currentSettings,
                activeTriviaSetId: triviaId
            };

            const response = await updateSettings(updatePayload);
            if (response.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: 'Paquete de trivia establecido como activo.'
                });
                onActiveTriviaChanged(triviaId);
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: response.message || 'No fue posible activar la trivia.'
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error al activar la trivia.'
            });
        }
    };

    /**
     * Opens form to add a new question to the selected trivia set.
     */
    const handleAddQuestion = () => {
        if (!selectedTriviaDetail) return;
        setQuestionToEdit(null);
        setIsQuestionModalOpen(true);
    };

    /**
     * Opens form to edit an existing question.
     */
    const handleEditQuestion = (question) => {
        setQuestionToEdit(question);
        setIsQuestionModalOpen(true);
    };

    /**
     * Prompts deletion of a question.
     */
    const handlePromptDeleteQuestion = (question) => {
        setConfirmConfig({
            message: `¿Deseas eliminar la adivinanza: "${question.instruction}"?`,
            actionToYesButton: async () => {
                const response = await deleteQuestion(question.id);
                if (response.success) {
                    showMessage({
                        type: MESSAGE_TYPE.SUCCESS,
                        text: 'Adivinanza eliminada exitosamente.'
                    });
                    if (selectedTriviaDetail) {
                        await loadTriviaDetail(selectedTriviaDetail.id);
                    }
                    await loadTriviaSets();
                    return { success: true };
                } else {
                    return { success: false, message: response.message || 'Error al eliminar la adivinanza.' };
                }
            },
            textYesButton: 'Sí, eliminar',
            textNoButton: 'Cancelar'
        });
        setShowConfirm(true);
    };

    return (
        <>
            <div className="cModal__Background" onClick={onClose}></div>
            <div className="cModal__Container" style={{ maxWidth: '900px' }}>
                <div className="cModal__Container__Content p-4 text-start">
                    <button
                        type="button"
                        className="Btn__Adsl--RedOutline cModal__Container__Content__CloseButton"
                        onClick={onClose}
                    >
                        X
                    </button>

                    <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                        <h4 className="fw-bold mb-0 text-dark d-flex align-items-center">
                            <i className="bi bi-collection-play text-danger me-2"></i>
                            Gestión de Paquetes de Trivias (Adivina Adivinador)
                        </h4>
                        {!isEditingTriviaSet && !selectedTriviaDetail && (
                            <button
                                type="button"
                                className="btn btn-sm btn-primary fw-bold"
                                onClick={handleStartCreateTriviaSet}
                            >
                                <i className="bi bi-plus-circle me-1"></i> Nueva Trivia
                            </button>
                        )}
                        {selectedTriviaDetail && (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => setSelectedTriviaDetail(null)}
                            >
                                <i className="bi bi-arrow-left me-1"></i> Volver a la lista
                            </button>
                        )}
                    </div>

                    {/* VIEW 1: TRIVIA SET CREATE / EDIT FORM */}
                    {isEditingTriviaSet && (
                        <div className="bg-light p-3 rounded-3 border mb-3">
                            <h5 className="fw-bold text-dark mb-3">
                                {triviaSetFormData.id ? 'Editar Paquete de Trivia' : 'Crear Nuevo Paquete de Trivia'}
                            </h5>
                            <form onSubmit={handleSaveTriviaSet}>
                                <div className="mb-3">
                                    <label className="form-label fw-bold small text-secondary mb-1">
                                        Título del Paquete: *
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Ej: Jugadores Históricos ADSL"
                                        value={triviaSetFormData.title}
                                        onChange={(e) => setTriviaSetFormData({ ...triviaSetFormData, title: e.target.value })}
                                        maxLength={200}
                                        required
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold small text-secondary mb-1">
                                        Descripción (Opcional):
                                    </label>
                                    <textarea
                                        className="form-control"
                                        rows={2}
                                        placeholder="Ej: Adivinanzas de jugadores y mascotas para el medio tiempo"
                                        value={triviaSetFormData.description}
                                        onChange={(e) => setTriviaSetFormData({ ...triviaSetFormData, description: e.target.value })}
                                        maxLength={1000}
                                    ></textarea>
                                </div>
                                <div className="form-check form-switch mb-3">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="triviaIsActiveSwitch"
                                        checked={triviaSetFormData.isActive}
                                        onChange={(e) => setTriviaSetFormData({ ...triviaSetFormData, isActive: e.target.checked })}
                                    />
                                    <label className="form-check-label small fw-bold text-dark" htmlFor="triviaIsActiveSwitch">
                                        Trivia activa y disponible
                                    </label>
                                </div>
                                <div className="d-flex justify-content-end gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-secondary"
                                        onClick={() => setIsEditingTriviaSet(false)}
                                        disabled={isSavingTriviaSet}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-sm btn-primary fw-bold"
                                        disabled={isSavingTriviaSet}
                                    >
                                        {isSavingTriviaSet ? 'Guardando...' : 'Guardar Paquete'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* VIEW 2: QUESTIONS LIST OF SELECTED TRIVIA */}
                    {selectedTriviaDetail && (
                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-3 p-2 bg-light rounded-3 border">
                                <div>
                                    <h5 className="fw-bold mb-0 text-dark">{selectedTriviaDetail.title}</h5>
                                    {selectedTriviaDetail.description && (
                                        <small className="text-muted">{selectedTriviaDetail.description}</small>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-success fw-bold"
                                    onClick={handleAddQuestion}
                                >
                                    <i className="bi bi-plus-lg me-1"></i> Agregar Adivinanza
                                </button>
                            </div>

                            {isLoadingDetail ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border text-primary" role="status"></div>
                                    <p className="text-muted small mt-2">Cargando adivinanzas...</p>
                                </div>
                            ) : (selectedTriviaDetail.questions || []).length === 0 ? (
                                <div className="text-center py-4 bg-light rounded-3 border">
                                    <i className="bi bi-card-image text-muted fs-1 d-block mb-2"></i>
                                    <p className="text-muted mb-2">Aún no hay adivinanzas registradas en este paquete.</p>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-primary"
                                        onClick={handleAddQuestion}
                                    >
                                        <i className="bi bi-plus-circle me-1"></i> Crear primera adivinanza
                                    </button>
                                </div>
                            ) : (
                                <div className="table-responsive" style={{ maxHeight: '380px', overflowY: 'auto' }}>
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="table-dark small">
                                            <tr>
                                                <th style={{ width: '50px' }}>#</th>
                                                <th style={{ width: '80px' }}>Silueta</th>
                                                <th style={{ width: '80px' }}>Respuesta</th>
                                                <th>Frase / Instrucción</th>
                                                <th>Opciones</th>
                                                <th style={{ width: '120px' }} className="text-end">Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedTriviaDetail.questions.map((q, idx) => (
                                                <tr key={q.id || idx}>
                                                    <td className="fw-bold text-secondary">{q.orderIndex !== undefined ? q.orderIndex + 1 : idx + 1}</td>
                                                    <td>
                                                        {q.imageUrl ? (
                                                            <img
                                                                src={resolveMediaUrl(q.imageUrl)}
                                                                alt="Silueta"
                                                                className="rounded border"
                                                                style={{ width: '50px', height: '50px', objectFit: 'contain', backgroundColor: '#f8f9fa' }}
                                                            />
                                                        ) : (
                                                            <span className="badge bg-secondary">Sin imagen</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {q.responseImageUrl ? (
                                                            <img
                                                                src={resolveMediaUrl(q.responseImageUrl)}
                                                                alt="Revelada"
                                                                className="rounded border"
                                                                style={{ width: '50px', height: '50px', objectFit: 'contain', backgroundColor: '#f8f9fa' }}
                                                            />
                                                        ) : (
                                                            <span className="badge bg-light text-dark border">
                                                                {q.responseText || 'Texto'}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="fw-bold text-dark small">{q.instruction}</div>
                                                        {q.responseText && (
                                                            <small className="text-muted d-block">Resp: {q.responseText}</small>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="small">
                                                            {(q.options || []).map((opt, optIdx) => (
                                                                <span
                                                                    key={optIdx}
                                                                    className={`badge me-1 ${opt.isCorrect ? 'bg-success' : 'bg-light text-dark border'}`}
                                                                >
                                                                    {opt.text}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </td>
                                                    <td className="text-end">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-primary me-1 p-1"
                                                            onClick={() => handleEditQuestion(q)}
                                                            title="Editar adivinanza"
                                                        >
                                                            <i className="bi bi-pencil-square"></i>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger p-1"
                                                            onClick={() => handlePromptDeleteQuestion(q)}
                                                            title="Eliminar adivinanza"
                                                        >
                                                            <i className="bi bi-trash"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* VIEW 3: TRIVIA SETS LIST */}
                    {!isEditingTriviaSet && !selectedTriviaDetail && (
                        <div>
                            {isLoading ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border text-danger" role="status"></div>
                                    <p className="text-muted small mt-2">Cargando paquetes de trivias...</p>
                                </div>
                            ) : triviaSets.length === 0 ? (
                                <div className="text-center py-4 bg-light rounded-3 border">
                                    <i className="bi bi-inbox text-muted fs-1 d-block mb-2"></i>
                                    <p className="text-muted mb-2">No hay paquetes de trivias creados.</p>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-primary"
                                        onClick={handleStartCreateTriviaSet}
                                    >
                                        <i className="bi bi-plus-circle me-1"></i> Crear primer paquete
                                    </button>
                                </div>
                            ) : (
                                <div className="table-responsive" style={{ maxHeight: '420px', overflowY: 'auto' }}>
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="table-dark small">
                                            <tr>
                                                <th>Título</th>
                                                <th>Descripción</th>
                                                <th className="text-center">Adivinanzas</th>
                                                <th className="text-center">Estado</th>
                                                <th style={{ width: '220px' }} className="text-end">Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {triviaSets.map((trivia) => {
                                                const isActive = currentActiveTriviaId === trivia.id;
                                                return (
                                                    <tr key={trivia.id} className={isActive ? 'table-success' : ''}>
                                                        <td>
                                                            <div className="fw-bold text-dark">{trivia.title}</div>
                                                            {isActive && (
                                                                <span className="badge bg-success small">
                                                                    <i className="bi bi-check-circle me-1"></i> Trivia Activa
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="small text-muted">{trivia.description || '—'}</td>
                                                        <td className="text-center">
                                                            <span className="badge bg-primary rounded-pill">
                                                                {trivia.questionsCount || 0}
                                                            </span>
                                                        </td>
                                                        <td className="text-center">
                                                            {trivia.isActive ? (
                                                                <span className="badge bg-success-subtle text-success border border-success">Activo</span>
                                                            ) : (
                                                                <span className="badge bg-secondary-subtle text-secondary border">Inactivo</span>
                                                            )}
                                                        </td>
                                                        <td className="text-end">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-info text-white me-1 p-1"
                                                                onClick={() => loadTriviaDetail(trivia.id)}
                                                                title="Ver / Gestionar preguntas"
                                                            >
                                                                <i className="bi bi-list-check me-1"></i> Preguntas
                                                            </button>
                                                            {!isActive && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-success me-1 p-1"
                                                                    onClick={() => handleSetActiveTrivia(trivia.id)}
                                                                    title="Establecer como trivia activa"
                                                                >
                                                                    <i className="bi bi-play-circle"></i> Activar
                                                                </button>
                                                            )}
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-primary me-1 p-1"
                                                                onClick={() => handleStartEditTriviaSet(trivia)}
                                                                title="Editar trivia"
                                                            >
                                                                <i className="bi bi-pencil"></i>
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-danger p-1"
                                                                onClick={() => handlePromptDeleteTriviaSet(trivia)}
                                                                title="Eliminar trivia"
                                                            >
                                                                <i className="bi bi-trash"></i>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="d-flex justify-content-end mt-4 pt-2 border-top">
                        <button
                            type="button"
                            className="btn btn-secondary px-4"
                            onClick={onClose}
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>

            {/* Individual Question Modal */}
            {isQuestionModalOpen && (
                <RiddleQuestionFormModal
                    isOpen={isQuestionModalOpen}
                    onClose={() => setIsQuestionModalOpen(false)}
                    questionToEdit={questionToEdit}
                    triviaSetId={selectedTriviaDetail?.id}
                    onQuestionSaved={async () => {
                        if (selectedTriviaDetail) {
                            await loadTriviaDetail(selectedTriviaDetail.id);
                        }
                        await loadTriviaSets();
                    }}
                />
            )}

            {/* Confirmation Modal */}
            {showConfirm && (
                <Confirm
                    message={confirmConfig.message}
                    actionToYesButton={confirmConfig.actionToYesButton}
                    textYesButton={confirmConfig.textYesButton}
                    textNoButton={confirmConfig.textNoButton}
                    setShowModal={setShowConfirm}
                    setConfirmData={() => {}}
                />
            )}
        </>
    );
};
